import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { t } from '@lingui/core/macro';
import z from 'zod';

import KEYS from '@rover/constants/src/platform/keys.constants';
import { Box, Button, Flex, Text } from '@rover/kibble/core';
import { useKibbleForm } from '@rover/kibble/forms';
import Badge from '@rover/kibble/official-patterns/Badge/Badge';
import BottomSheet from '@rover/kibble/official-patterns/BottomSheet/BottomSheet';
import Modal from '@rover/kibble/official-patterns/Modal';
import FancyRadio from '@rover/kibble/patterns/FancyRadio';
import Row from '@rover/kibble/patterns/Row';
import useTheme from '@rover/kibble/themes/useTheme';
import { EnhancedPersonSitterProfileService } from '@rover/rsdk/src/apiClient/latest/model/enhancedPersonSitterProfileService';

import { getUniqueServicesSortedByPriority, serviceIconToGraphic } from './utils';

const schema = z.object({
  serviceSlug: z.preprocess((v) => v || '', z.string().min(1, 'Required')),
});

type VariantType = 'mobile' | 'desktop';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  services: EnhancedPersonSitterProfileService[];
  selectedServiceSlug: string | undefined;
  onSave: (slug: string) => void;
  variant: VariantType;
};

const RevampServiceSelector = ({
  isOpen,
  onClose,
  services,
  selectedServiceSlug,
  onSave,
  variant,
}: Props): JSX.Element => {
  const { textColor } = useTheme();
  const { watch, reset, setValue } = useKibbleForm({
    schema,
    defaultValues: { serviceSlug: selectedServiceSlug ?? '' },
  });

  const pendingServiceSlug = watch('serviceSlug');

  const availableServices = useMemo(() => getUniqueServicesSortedByPriority(services), [services]);

  const filteredServices = useMemo(
    () => [
      ...availableServices.filter((service) => !service.isAway),
      ...availableServices.filter((service) => service.isAway),
    ],
    [availableServices]
  );

  useEffect(() => {
    if (isOpen) {
      reset({ serviceSlug: selectedServiceSlug ?? '' });
    }
  }, [isOpen, selectedServiceSlug, reset]);

  const rowRefs = useRef<Array<HTMLElement | null>>([]);
  const saveButtonRef = useRef<HTMLElement | null>(null);

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent, index: number, slug: string) => {
      switch (e.key) {
        case KEYS.ENTER:
        case KEYS.SPACE:
          e.preventDefault();
          setValue('serviceSlug', slug);
          break;
        case KEYS.DOWN:
          e.preventDefault();
          if (index < filteredServices.length - 1) {
            rowRefs.current[index + 1]?.focus();
          } else {
            saveButtonRef.current?.focus();
          }
          break;
        case KEYS.UP:
          e.preventDefault();
          rowRefs.current[index - 1]?.focus();
          break;
        default:
          break;
      }
    },
    [setValue, filteredServices.length]
  );

  const handleSaveKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === KEYS.UP) {
        e.preventDefault();
        rowRefs.current[filteredServices.length - 1]?.focus();
      }
    },
    [filteredServices.length]
  );

  const handleSave = useCallback(() => {
    if (pendingServiceSlug) {
      onSave(pendingServiceSlug);
    }
    onClose();
  }, [pendingServiceSlug, onSave, onClose]);

  const renderServiceSelector = (): JSX.Element => {
    return (
      <>
        <Flex justifyContent="center">
          <Text size="200" fontWeight="semibold" pb="3x">
            {t`Select a service`}
          </Text>
        </Flex>
        {filteredServices.length > 0 && (
          <Box>
            {filteredServices.map((service, index) => {
              const IconComponent = serviceIconToGraphic[service.icon];
              const awayText = t`Away`;
              const comingSoonText = t`Coming Soon`;
              const isComingSoon = !service.isApproved;
              const showAsAway = service.isAway && !isComingSoon;
              const statuses: string[] = [];
              if (isComingSoon) {
                statuses.push(comingSoonText);
              } else if (showAsAway) {
                statuses.push(awayText);
              }
              const ariaLabel = statuses.length
                ? `${service.displayName} (${statuses.join(', ')})`
                : service.displayName;
              const itemTextColor = showAsAway ? 'tertiary' : 'primary';
              return (
                <Row
                  key={service.slug}
                  ref={(el: HTMLElement | null) => {
                    rowRefs.current[index] = el;
                  }}
                  leftItem={
                    IconComponent ? (
                      <Box width="24px" height="24px">
                        <IconComponent
                          width={24}
                          height={24}
                          fill={showAsAway ? textColor.tertiary : textColor.primary}
                        />
                      </Box>
                    ) : undefined
                  }
                  label={
                    <Flex flexDirection="row" gap="2x" alignItems="center">
                      <Text size="200" fontWeight="semibold" textColor={itemTextColor}>
                        {service.displayName}
                      </Text>
                      {showAsAway && <Badge text={awayText} variant="tertiary" />}
                      {isComingSoon && (
                        <Badge
                          boxProps={{
                            testID: `service-selector-coming-soon-badge-${service.slug}`,
                          }}
                          text={comingSoonText}
                          variant="default"
                        />
                      )}
                    </Flex>
                  }
                  sublabel={
                    service.description ? (
                      <Text size="100" textColor={itemTextColor}>
                        {service.description}
                      </Text>
                    ) : undefined
                  }
                  rightItem={
                    <FancyRadio
                      hideLabel
                      label={service.displayName}
                      name="serviceSlug"
                      id={`service-${service.slug}`}
                      actualId={`service-${service.slug}`}
                      value={service.slug}
                      checked={pendingServiceSlug === service.slug}
                      disabled={false}
                      validationType="inline"
                      onChange={(value: string) => setValue('serviceSlug', value)}
                      style={{ margin: '0', padding: '0' }}
                    />
                  }
                  onClick={() => setValue('serviceSlug', service.slug)}
                  webProps={{
                    onKeyDown: (e: React.KeyboardEvent) => handleRowKeyDown(e, index, service.slug),
                  }}
                  ariaLabel={ariaLabel}
                />
              );
            })}
          </Box>
        )}
      </>
    );
  };

  if (variant === 'desktop') {
    return (
      <Modal isOpen={isOpen} onRequestClose={onClose}>
        {renderServiceSelector()}
        <Button
          fullWidth
          variant="primary"
          onPress={handleSave}
          mt="4x"
          webProps={{ ref: saveButtonRef, onKeyDown: handleSaveKeyDown }}
        >
          {t`Save`}
        </Button>
      </Modal>
    );
  }
  return (
    <BottomSheet
      isOpen={isOpen}
      onRequestClose={onClose}
      hideCloseIcon
      FooterComponent={
        <Button
          variant="primary"
          size="small"
          fullWidth
          mt="6x"
          onPress={handleSave}
          webProps={{ ref: saveButtonRef, onKeyDown: handleSaveKeyDown }}
        >
          {t`Save`}
        </Button>
      }
    >
      {renderServiceSelector()}
    </BottomSheet>
  );
};

export default RevampServiceSelector;
